import { useCallback, useRef, useState } from 'react';
import { useVisualizerStore } from '@/stores/useVisualizerStore';
import { handleFileSelect } from '@/utils/upload';
import { useTenant } from '@/utils/tenant';
import { isEmbedded } from '@/utils/config';


const IS_EMBED = isEmbedded();

export function UploadScreen() {
  const setImage = useVisualizerStore((s) => s.setImage);
  const tenant = useTenant();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const onFile = useCallback(
    async (file: File) => {
      const image = await handleFileSelect(file);
      if (image) {
        setImage(image);
      }
    },
    [setImage]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) onFile(file);
    },
    [onFile]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) onFile(file);
    },
    [onFile]
  );

  const triggerFileInput = useCallback(() => {
    // Clear the input before clicking so Safari treats every selection as a
    // new change event (avoids the first-click-does-nothing bug).
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    inputRef.current?.click();
  }, []);

  return (
    <div className={`flex flex-col items-center px-4 py-8 bg-gradient-radial-blue bg-particles ${IS_EMBED ? 'min-h-full' : 'h-full overflow-y-auto'}`}>
      <div className="w-full max-w-md perspective-1000">
        <div className="mb-6 text-center animate-slide-up">
          <h2 className="mb-3 text-3xl font-extrabold tracking-tight text-slate-900">
            See Your <span className="text-bss-blue">New Finish</span>
          </h2>
          <p className="text-slate-500">
            Upload a photo of your countertop to preview resurfacing finishes and speckle patterns instantly.
          </p>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={triggerFileInput}
          className={`card-3d glass-strong cursor-pointer rounded-3xl border-2 border-dashed p-10 text-center transition-all duration-300 preserve-3d animate-slide-up ${
            isDragging
              ? 'border-bss-blue bg-blue-50/80 shadow-glow-blue scale-[1.01]'
              : 'border-slate-200 hover:border-bss-blue/50 hover:shadow-glow-blue-sm hover:bg-blue-50/20'
          }`}
          style={{ animationDelay: '80ms' }}
        >
          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/30 preserve-3d animate-float">
            <svg
              className="h-10 w-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
          </div>
          <p className="mb-1 text-lg font-semibold text-slate-900">
            {isDragging ? 'Drop your photo here' : 'Drag & drop your photo'}
          </p>
          <p className="text-sm text-slate-500">or click to browse</p>
          <p className="mt-2 text-xs text-slate-400">JPG, PNG, HEIC · Up to 20MB</p>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
          onChange={handleChange}
          className="sr-only"
        />

        <div className="mt-8 flex flex-col gap-4">
          <button
            onClick={triggerFileInput}
            className="group relative flex items-center justify-center gap-2 rounded-2xl bg-bss-blue px-6 py-4 text-base font-bold text-white shadow-lg shadow-blue-600/30 transition-all duration-200 hover:bg-bss-blue-dark hover:shadow-blue-600/40 hover:-translate-y-1 hover:scale-[1.01] active:translate-y-0 active:scale-[0.99] animate-slide-up"
            style={{ animationDelay: '130ms' }}
          >
            <svg className="h-5 w-5 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
              />
            </svg>
            Take a Photo
          </button>

          <div className="flex items-center justify-center gap-4 text-xs text-slate-400 animate-slide-up" style={{ animationDelay: '180ms' }}>
            <span className="flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1.5 shadow-sm transition-all hover:shadow-md hover:bg-white hover:-translate-y-0.5">
              <svg className="h-4 w-4 text-bss-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
              </svg>
              Secure CounterVision processing
            </span>
            <span className="flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1.5 shadow-sm transition-all hover:shadow-md hover:bg-white hover:-translate-y-0.5">
              <svg className="h-4 w-4 text-bss-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                />
              </svg>
              No account needed
            </span>
          </div>

          <p className="text-center text-[10px] leading-relaxed text-slate-300">
            By uploading a photo, your before &amp; after preview images may be shared with{' '}
            {tenant.website ? (
              <a
                href={tenant.website}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-slate-400 transition-colors"
              >
                {tenant.businessName}
              </a>
            ) : (
              <span>{tenant.businessName}</span>
            )}{' '}
            solely to provide you with a resurfacing estimate. Your images are never used
            for AI training or shared with any third parties. Your countertop images are deleted after preview generation.
          </p>
        </div>
      </div>
    </div>
  );
}
